// src/components/EditEventModal.jsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faSave,
  faCalendarAlt,
  faMapMarkerAlt,
  faUserTie,
  faDollarSign,
  faTag,
  faUsers,
  faChair,
  faStore,
  faParking,
  faUtensils,
  faRestroom,
  faMedkit,
  faMusic,
  faEdit,
  faTicketAlt,
  faShoppingCart,
  faMoneyBillWave,
  faBuilding,
  faPercent,
  faCheckCircle,
  faPhone,
  faEnvelope,
  faCreditCard,
  faCalendarCheck,
  faPencilAlt,
  faTrashAlt,
  faEye,
  faPlus,
  faBan,
  faChartPie,
  faBrush,
  faShieldAlt,
  faTruck,
  faEllipsisH,
  faWallet,
  faCoins,
  faFileExport,
  faClone
} from '@fortawesome/free-solid-svg-icons';
import './EditEventmodal.css';

function EditEventModal({ event, onClose, onSave }) {
  const [formData, setFormData] = useState({
    eventId: null,
    eventName: '',
    eventDate: '',
    spansour: '',
    place: '',
    budget: '',
    highlights: '',
    stallPrice: '',
    ticketPrice: '',
    standingCapacity: '',
    hasSeating: true,
    seatingType: 'full',
    seatRows: 10,
    seatColumns: 10,
    totalSeats: 100,
    showEntrance: true,
    showExit: true,
    showStage: true,
    showParking: true,
    parkingZones: 2,
    hasVIPParking: false,
    showFoodCourt: false,
    showRestrooms: false,
    showFirstAid: false
  });

  const [activeTab, setActiveTab] = useState('basic');
  const [isSaving, setIsSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize for responsive tabs
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (event) {
      setFormData({
        eventId: event.eventId,
        eventName: event.eventName || '',
        eventDate: event.eventDate || '',
        spansour: event.spansour || '',
        place: event.place || '',
        budget: event.budget || '',
        highlights: event.highlights || '',
        stallPrice: event.stallPrice || 8000,
        ticketPrice: event.ticketPrice || 1000,
        standingCapacity: event.standingCapacity || 0,
        hasSeating: event.hasSeating !== undefined ? event.hasSeating : true,
        seatingType: event.seatingType || 'full',
        seatRows: event.seatRows || 10,
        seatColumns: event.seatColumns || 10,
        totalSeats: event.totalSeats || 100,
        showEntrance: event.showEntrance !== undefined ? event.showEntrance : true,
        showExit: event.showExit !== undefined ? event.showExit : true,
        showStage: event.showStage !== undefined ? event.showStage : true,
        showParking: event.showParking !== undefined ? event.showParking : true,
        parkingZones: event.parkingZones || 2,
        hasVIPParking: event.hasVIPParking || false,
        showFoodCourt: event.showFoodCourt || false,
        showRestrooms: event.showRestrooms || false,
        showFirstAid: event.showFirstAid || false
      });
    }
  }, [event]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? '' : Number(value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      // Format date to YYYY-MM-DD
      const formatDate = (dateString) => {
        if (!dateString) return new Date().toISOString().split('T')[0];
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      };

      const updatedEvent = {
        eventId: formData.eventId,
        eventName: formData.eventName,
        eventDate: formatDate(formData.eventDate),
        spansour: formData.spansour,
        place: formData.place,
        budget: Number(formData.budget) || 0,
        highlights: formData.highlights || '',
        stallPrice: Number(formData.stallPrice) || 8000,
        ticketPrice: Number(formData.ticketPrice) || 1000,
        standingCapacity: Number(formData.standingCapacity) || 0,
        hasSeating: formData.hasSeating,
        seatingType: formData.seatingType,
        seatRows: Number(formData.seatRows) || 10,
        seatColumns: Number(formData.seatColumns) || 10,
        totalSeats: formData.hasSeating ? (Number(formData.seatRows) * Number(formData.seatColumns)) : 0,
        showEntrance: formData.showEntrance,
        showExit: formData.showExit,
        showStage: formData.showStage,
        showParking: formData.showParking,
        parkingZones: Number(formData.parkingZones) || 2,
        hasVIPParking: formData.hasVIPParking,
        showFoodCourt: formData.showFoodCourt,
        showRestrooms: formData.showRestrooms,
        showFirstAid: formData.showFirstAid,
        isCustomLayout: event.isCustomLayout || false,
        stallPattern: event.stallPattern || 'full'
      };

      console.log("Sending updated event:", updatedEvent);
      
      await onSave(updatedEvent);
      onClose();
    } catch (error) {
      console.error("Error saving event:", error);
      alert("Failed to save event: " + (error.message || "Please try again"));
    } finally {
      setIsSaving(false);
    }
  };

  const updateSeatCount = (rows, cols) => {
    return rows * cols;
  };

  const handleRowsChange = (e) => {
    const rows = parseInt(e.target.value) || 1;
    setFormData(prev => ({
      ...prev,
      seatRows: rows,
      totalSeats: updateSeatCount(rows, prev.seatColumns)
    }));
  };

  const handleColumnsChange = (e) => {
    const cols = parseInt(e.target.value) || 1;
    setFormData(prev => ({
      ...prev,
      seatColumns: cols,
      totalSeats: updateSeatCount(prev.seatRows, cols)
    }));
  };

  if (!event) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="edit-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faEdit} />
            {isMobile ? 'Edit Event' : `Edit Event: ${event.eventName}`}
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Tab Navigation - Responsive */}
          <div className="modal-tabs">
            <button
              type="button"
              className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`}
              onClick={() => setActiveTab('basic')}
            >
              <span className="tab-icon">📋</span>
              <span className="tab-text">{isMobile ? 'Basic' : 'Basic Details'}</span>
            </button>
            <button
              type="button"
              className={`tab-btn ${activeTab === 'seating' ? 'active' : ''}`}
              onClick={() => setActiveTab('seating')}
            >
              <span className="tab-icon">🪑</span>
              <span className="tab-text">{isMobile ? 'Seating' : 'Seating & Map'}</span>
            </button>
            {!event.isCustomLayout && (
              <button
                type="button"
                className={`tab-btn ${activeTab === 'stalls' ? 'active' : ''}`}
                onClick={() => setActiveTab('stalls')}
              >
                <span className="tab-icon">🏪</span>
                <span className="tab-text">{isMobile ? 'Stalls' : 'Stall Config'}</span>
              </button>
            )}
          </div>

          <div className="modal-body">
            {/* Basic Details Tab */}
            {activeTab === 'basic' && (
              <div className="tab-pane">
                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faTag} />
                      Event Name *
                    </label>
                    <input
                      type="text"
                      name="eventName"
                      value={formData.eventName}
                      onChange={handleChange}
                      required
                      placeholder="Enter event name"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      Event Date *
                    </label>
                    <input
                      type="date"
                      name="eventDate"
                      value={formData.eventDate}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faUserTie} />
                      Sponsor
                    </label>
                    <input
                      type="text"
                      name="spansour"
                      value={formData.spansour}
                      onChange={handleChange}
                      placeholder="Sponsor name"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                      Venue
                    </label>
                    <input
                      type="text"
                      name="place"
                      value={formData.place}
                      onChange={handleChange}
                      placeholder="Event venue"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faMoneyBillWave} />
                      Budget (₹)
                    </label>
                    <input
                      type="number"
                      name="budget"
                      value={formData.budget}
                      onChange={handleNumberChange}
                      min="0"
                      step="1000"
                      placeholder="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faStore} />
                      Stall Price (₹)
                    </label>
                    <input
                      type="number"
                      name="stallPrice"
                      value={formData.stallPrice}
                      onChange={handleNumberChange}
                      min="0"
                      step="100"
                      placeholder="8000"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faTicketAlt} />
                      Ticket Price (₹)
                    </label>
                    <input
                      type="number"
                      name="ticketPrice"
                      value={formData.ticketPrice}
                      onChange={handleNumberChange}
                      min="0"
                      step="50"
                      placeholder="1000"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faUsers} />
                      Standing Capacity
                    </label>
                    <input
                      type="number"
                      name="standingCapacity"
                      value={formData.standingCapacity}
                      onChange={handleNumberChange}
                      min="0"
                      placeholder="0 = unlimited"
                    />
                    <small>Maximum standing tickets (0 = unlimited)</small>
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>Highlights</label>
                  <textarea
                    name="highlights"
                    value={formData.highlights}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Event highlights..."
                  />
                </div>
              </div>
            )}

            {/* Seating & Map Tab */}
            {activeTab === 'seating' && (
              <div className="tab-pane">
                <div className="toggle-section">
                  <label className="toggle-label">
                    <span>Enable Seating</span>
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        name="hasSeating"
                        checked={formData.hasSeating}
                        onChange={handleChange}
                      />
                      <span className="toggle-slider"></span>
                    </div>
                  </label>
                  <p className="toggle-description">
                    {formData.hasSeating 
                      ? '✅ Assigned seating with row and column layout'
                      : '❌ General admission - Standing only'}
                  </p>
                </div>

                {formData.hasSeating && (
                  <>
                    <div className="form-group">
                      <label>Seating Type</label>
                      <select
                        name="seatingType"
                        value={formData.seatingType}
                        onChange={handleChange}
                      >
                        <option value="full">Full Seating - All seats available</option>
                        <option value="partial">Partial Seating - Some seats available</option>
                        <option value="theater">Theater Style</option>
                        <option value="classroom">Classroom Style</option>
                      </select>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Rows (1-50)</label>
                        <input
                          type="number"
                          name="seatRows"
                          value={formData.seatRows}
                          onChange={handleRowsChange}
                          min="1"
                          max="50"
                        />
                      </div>
                      <div className="form-group">
                        <label>Columns (1-50)</label>
                        <input
                          type="number"
                          name="seatColumns"
                          value={formData.seatColumns}
                          onChange={handleColumnsChange}
                          min="1"
                          max="50"
                        />
                      </div>
                    </div>

                    <div className="total-seats-display">
                      <span>Total Seats:</span>
                      <strong>{formData.seatRows * formData.seatColumns}</strong>
                      <small>({formData.seatRows} × {formData.seatColumns})</small>
                    </div>
                  </>
                )}

                <h3 className="section-title">Map Elements</h3>
                
                <div className="map-elements-grid">
                  <div className="element-group">
                    <h4>Essential Elements</h4>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="showEntrance"
                        checked={formData.showEntrance}
                        onChange={handleChange}
                      />
                      <span>🚪 Entrance (North)</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="showExit"
                        checked={formData.showExit}
                        onChange={handleChange}
                      />
                      <span>🚪 Exit (South)</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="showStage"
                        checked={formData.showStage}
                        onChange={handleChange}
                      />
                      <span>🎪 Stage</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="showParking"
                        checked={formData.showParking}
                        onChange={handleChange}
                      />
                      <span>🅿️ Parking</span>
                    </label>
                  </div>

                  <div className="element-group">
                    <h4>Amenities</h4>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="showFoodCourt"
                        checked={formData.showFoodCourt}
                        onChange={handleChange}
                      />
                      <span>🍔 Food Court</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="showRestrooms"
                        checked={formData.showRestrooms}
                        onChange={handleChange}
                      />
                      <span>🚻 Restrooms</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="showFirstAid"
                        checked={formData.showFirstAid}
                        onChange={handleChange}
                      />
                      <span>🏥 First Aid</span>
                    </label>
                  </div>
                </div>

                {formData.showParking && (
                  <div className="parking-config">
                    <h4>Parking Configuration</h4>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Number of Zones</label>
                        <select
                          name="parkingZones"
                          value={formData.parkingZones}
                          onChange={handleNumberChange}
                        >
                          <option value="1">1 Zone</option>
                          <option value="2">2 Zones</option>
                          <option value="3">3 Zones</option>
                        </select>
                      </div>
                      <div className="form-group checkbox-group">
                        <label className="checkbox-label vip-parking">
                          <input
                            type="checkbox"
                            name="hasVIPParking"
                            checked={formData.hasVIPParking}
                            onChange={handleChange}
                          />
                          <span>✨ VIP Parking Available</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Stall Config Tab (only for predefined layouts) */}
            {activeTab === 'stalls' && !event.isCustomLayout && (
              <div className="tab-pane">
                <div className="info-message">
                  <FontAwesomeIcon icon={faBrush} />
                  <p>Stall layout can only be edited through the Layout Builder.</p>
                  <button 
                    type="button"
                    className="link-btn"
                    onClick={() => {
                      onClose();
                      // Navigate to layout builder with event data
                      const eventData = {
                        eventId: formData.eventId,
                        name: formData.eventName,
                        date: formData.eventDate,
                        spansaour: formData.spansour,
                        venue: formData.place,
                        budget: formData.budget,
                        highlights: formData.highlights,
                        stallPrice: formData.stallPrice,
                        ticketPrice: formData.ticketPrice,
                        isCustomLayout: true,
                        isEditMode: true
                      };
                      sessionStorage.setItem('pendingEventData', JSON.stringify(eventData));
                      window.location.href = '/admin/layout-builder';
                    }}
                  >
                    Go to Layout Builder →
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="save-btn" disabled={isSaving}>
              <FontAwesomeIcon icon={faSave} />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditEventModal;